/* ==========================================================================
   Dropper Post Length Selector

   Replaces the custom-html blob that lived in
   templates/page.dropper-post-length-selector.json.

   WHAT CHANGED, AND WHY IT MATTERS
   --------------------------------
   The old version held a hardcoded list of purchasable sizes:

       basePostSizesStandard = [90, 120, 150, 180, 210, 240]

   applied to every standard diameter. That list had already drifted from the
   catalogue: 34.9mm has no 90mm post and never has, so the calculator could
   recommend a product that does not exist. Harmless while the result was only
   a printed number; a dead end the moment it became a button.

   Here the geometry stays hardcoded — stack heights and insert lengths are
   engineering facts about the posts — but WHICH SIZES ARE PURCHASABLE comes
   from the store, rendered into the page by Liquid. The calculator therefore
   cannot recommend something the store does not sell, it reflects each
   region's own stock, and it self-corrects if a variant is added or retired.

   TERMINOLOGY
   -----------
   Customer-facing copy says "reduce the travel" and never "shim". At OneUp a
   shim is a DIAMETER shim (packing a 30.9mm post out to a 31.6mm or 34.9mm
   frame), which is a different part and a different recommendation. The pins
   that shorten a post's stroke are travel reducing pins, and they ship in the
   box.
   ========================================================================== */

(function () {
  'use strict';

  /* --- post geometry ----------------------------------------------------
   * From the V3 table on the Specs tab of the "Dropper Insertion Calculator"
   * Google Sheet, which is the source of truth for these numbers - update it
   * there first, then here. Each row is one achievable drop, including drops
   * only reachable by fitting the travel reducing pins: a 240mm post also
   * appears at 230 and 220, with the lower stack height that reducing it
   * produces.
   *
   * drop        travel in mm                                   (sheet: Drop)
   * stack       collar to saddle rails, post fully inserted -
   *             the lowest ride height it can give             (B: Stack Height)
   * maxExt      highest ride height before the post is below
   *             its minimum insertion                          (C: Max Extension)
   * insert      length below the collar when fully inserted,
   *             including the cable actuator                   (F: Insert Length)
   *
   * insert + stack is the post's total length (A), which is what has to fit
   * between the saddle rails and the first obstruction in the frame.
   */
  var POSTS_STANDARD = [
    { drop: 240, stack: 270, maxExt: 454, insert: 346 },
    { drop: 230, stack: 260, maxExt: 444, insert: 346 },
    { drop: 220, stack: 250, maxExt: 434, insert: 346 },
    { drop: 210, stack: 240, maxExt: 404, insert: 311 },
    { drop: 200, stack: 230, maxExt: 394, insert: 311 },
    { drop: 190, stack: 220, maxExt: 384, insert: 311 },
    { drop: 180, stack: 210, maxExt: 357, insert: 279 },
    { drop: 170, stack: 200, maxExt: 347, insert: 279 },
    { drop: 160, stack: 190, maxExt: 337, insert: 279 },
    { drop: 150, stack: 180, maxExt: 307, insert: 244 },
    { drop: 140, stack: 170, maxExt: 297, insert: 244 },
    { drop: 130, stack: 160, maxExt: 287, insert: 244 },
    { drop: 120, stack: 150, maxExt: 254, insert: 206 },
    { drop: 110, stack: 140, maxExt: 244, insert: 206 },
    { drop: 100, stack: 130, maxExt: 234, insert: 206 },
    { drop: 90, stack: 120, maxExt: 204, insert: 171 },
    { drop: 80, stack: 110, maxExt: 194, insert: 171 },
    { drop: 70, stack: 100, maxExt: 184, insert: 171 }
  ];

  var POSTS_272 = [
    { drop: 120, stack: 150, maxExt: 279, insert: 216 },
    { drop: 110, stack: 140, maxExt: 269, insert: 216 },
    { drop: 100, stack: 130, maxExt: 259, insert: 216 },
    { drop: 90, stack: 120, maxExt: 205, insert: 181 },
    { drop: 80, stack: 110, maxExt: 195, insert: 181 },
    { drop: 70, stack: 100, maxExt: 185, insert: 181 }
  ];

  /* The 34.9mm post's compressed stack height is 25mm against 30mm for the
   * other diameters (Product Specs tab), so its saddle sits 5mm lower at every
   * setting and it fits a ride height 5mm lower.
   *
   * That is the only published difference. Whether the rest of the post is
   * also 5mm shorter is not, so each of the other two checks takes whichever
   * assumption is safer: the post still needs its full length of frame (no 5mm
   * credit on insertion depth), and it may be raised 5mm less far before
   * reaching minimum insertion. */
  var STACK_REDUCTION_349 = 5;

  /* A post ships with pins that take up to 20mm out of its travel. This is the
   * rule that makes "which post do I buy for a 170mm drop" answerable: the
   * 180mm post, reduced by 10mm. It is also what rules OUT buying a 120mm post
   * for a 90mm drop — 30mm is more than the pins can remove. */
  var MAX_TRAVEL_REDUCTION = 20;

  /* Diameter used for the fallback recommendation when a customer's frame
   * diameter has no post that fits them at all. A 30.9mm post plus a diameter
   * shim fits a larger frame; the reverse is not possible. */
  var SHIM_BASE_DIAMETER = '30.9mm';

  function num(str) {
    var f = parseFloat(String(str).replace(/[^0-9.]/g, ''));
    return isNaN(f) ? null : f;
  }

  /* --- component --------------------------------------------------------- */

  function Calculator(root) {
    this.root = root;
    this.context = root.getAttribute('data-dlc-context') || 'page';
    this.results = root.querySelector('[data-dlc-results]');
    this.calcBtn = root.querySelector('[data-dlc-calculate]');
    this.errorEl = root.querySelector('[data-dlc-error]');
    this.summary = root.querySelector('[data-dlc-summary]');
    this.busy = false;
    this.hasResult = false;
    this.data = this.readData();
    if (!this.data) return;
    this.bind();
  }

  Calculator.prototype.readData = function () {
    var id = this.root.getAttribute('id');
    var tag = document.querySelector('[data-dlc-data-for="' + id + '"]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return null;
    }
  };

  Calculator.prototype.bind = function () {
    var self = this;

    if (this.calcBtn) {
      this.calcBtn.addEventListener('click', function () {
        self.calculate();
      });
    }

    // Changing diameter re-runs the calculation, but only where the answer is
    // on screen beside the form. On a phone the form and the answer take turns,
    // so a re-run there would swap the form away while the customer is still
    // editing it - they press Calculate when they are ready instead.
    var radios = this.root.querySelectorAll('[data-dlc-diameter]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () {
        if (self.hasResult && sideBySide() && self.readInputs()) {
          self.calculate();
        } else {
          self.markStale(true);
        }
      });
    }

    var change = this.root.querySelector('[data-dlc-change]');
    if (change) {
      change.addEventListener('click', function () {
        self.showView('form');
      });
    }

    // Enter in either number field runs it, which is what people expect.
    // Typing in one after a result marks that result stale - see markStale.
    var inputs = this.root.querySelectorAll('[data-dlc-ride], [data-dlc-insert]');
    for (var j = 0; j < inputs.length; j++) {
      inputs[j].addEventListener('input', function () {
        self.markStale(true);
      });
      inputs[j].addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          self.calculate();
        }
      });
    }

    // Delegated: results are re-rendered on every calculation, so listeners
    // cannot be attached to the buttons themselves.
    this.results.addEventListener('click', function (event) {
      var pick = event.target.closest('[data-dlc-pick]');
      if (pick) {
        self.showOption(pick.getAttribute('data-dlc-pick'));
        return;
      }

      var choose = event.target.closest('[data-dlc-choose]');
      if (choose) {
        event.preventDefault();
        self.chooseVariant(choose);
      }
    });
  };

  Calculator.prototype.readInputs = function () {
    var rideEl = this.root.querySelector('[data-dlc-ride]');
    var insertEl = this.root.querySelector('[data-dlc-insert]');
    var diaEl = this.root.querySelector('[data-dlc-diameter]:checked');
    if (!rideEl || !insertEl || !diaEl) return null;

    var ride = parseFloat(rideEl.value);
    var insert = parseFloat(insertEl.value);
    if (isNaN(ride) || isNaN(insert) || ride <= 0 || insert <= 0) return null;

    return { ride: ride, insert: insert, diameter: diaEl.value };
  };

  /* Geometry for a diameter. 34.9mm is the standard table with its lower stack
   * applied - see STACK_REDUCTION_349. Raising insert by the same amount keeps
   * insert + stack, the total length, unchanged. */
  Calculator.prototype.postsFor = function (diameter) {
    var d = num(diameter);
    if (d === 27.2) return POSTS_272;
    if (d !== 34.9) return POSTS_STANDARD;
    return POSTS_STANDARD.map(function (p) {
      return {
        drop: p.drop,
        stack: p.stack - STACK_REDUCTION_349,
        maxExt: p.maxExt - STACK_REDUCTION_349,
        insert: p.insert + STACK_REDUCTION_349
      };
    });
  };

  /* --- the fit test -------------------------------------------------------
   * A drop fits when the rider's ride height is between the post's lowest
   * (stack) and highest (maxExt) settings, and the whole post fits above the
   * first obstruction in the frame. A ride height above maxExt rules the post
   * out: it would sit below its minimum insertion. */
  Calculator.prototype.compatibleDrops = function (diameter, ride, insert) {
    var posts = this.postsFor(diameter);
    return posts.filter(function (p) {
      return (
        ride >= p.stack &&
        p.insert + p.stack <= ride + insert &&
        ride <= p.maxExt
      );
    });
  };

  /* Sizes this store actually sells in a given diameter, longest first. */
  Calculator.prototype.travelsFor = function (diameter) {
    var out = [];
    var variants = this.data.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].diameter === diameter) out.push(variants[i]);
    }
    return out.sort(function (a, b) {
      return num(b.travel) - num(a.travel);
    });
  };

  /* Map a calculated drop onto a post the store sells.
   * Exact size if it exists, otherwise the smallest sold size above it that
   * the travel reducing pins can bring down far enough. Null when neither
   * applies — which is how 34.9mm x 90mm gets caught. */
  Calculator.prototype.purchasableFor = function (drop, diameter) {
    var sold = this.travelsFor(diameter);
    var best = null;

    for (var i = 0; i < sold.length; i++) {
      var t = num(sold[i].travel);
      if (t === drop) return { variant: sold[i], base: t, reduceTo: null };
      if (t > drop && t - drop <= MAX_TRAVEL_REDUCTION) {
        if (!best || t < num(best.travel)) best = sold[i];
      }
    }

    if (!best) return null;
    return { variant: best, base: num(best.travel), reduceTo: drop };
  };

  /* --- calculate ---------------------------------------------------------
   * Every route in - the button, Enter in a field, changing diameter - comes
   * through here. A valid calculation shows a loading state on the button for
   * LOADING_MS, then the result: beside the form on wide screens, in its place
   * on narrow ones. Nothing scrolls - the layout is sized so it never has to.
   * Invalid input is reported at once with no loading state, under the
   * button, where the customer is already looking. */

  var LOADING_MS = 500;
  var RECALCULATE_LABEL = 'Recalculate';

  /* Must match the breakpoint in dropper-length-calculator.css, above which
   * the form and the result sit side by side. */
  function sideBySide() {
    return !!(window.matchMedia && window.matchMedia('(min-width: 769px)').matches);
  }

  Calculator.prototype.calculate = function () {
    var self = this;
    if (this.busy) return;

    var input = this.readInputs();
    if (!input) {
      this.showError('Please enter valid positive numbers for both measurements.');
      return;
    }

    this.showError(null);
    this.startLoading();
    window.setTimeout(function () {
      // Re-read rather than reuse: a diameter tapped during the wait is
      // ignored by the busy guard above, so pick it up here instead.
      self.render(self.readInputs() || input);
      self.markStale(false);
      self.endLoading();
      self.hasResult = true;
      self.showView('result');
    }, LOADING_MS);
  };

  /* Wide screens keep the answer on screen beside the form, so once a
   * measurement is edited the answer beside it no longer matches the numbers.
   * Dim it and say so until Recalculate is pressed, rather than leave a
   * confident answer to the wrong question. Narrow screens hide the answer
   * while the form is shown, so there the flag is set but never seen. */
  Calculator.prototype.markStale = function (stale) {
    if (!this.hasResult && stale) return;
    var note = this.root.querySelector('[data-dlc-stale]');
    if (stale) {
      this.results.setAttribute('data-dlc-stale', 'true');
    } else {
      this.results.removeAttribute('data-dlc-stale');
    }
    if (note) note.hidden = !stale;
  };

  Calculator.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  };

  /* On narrow screens the form and the result take turns. Focus follows the
   * swap - to the result, then back to the first measurement - so keyboard and
   * screen reader users land on what just appeared rather than on a control
   * that is no longer displayed. preventScroll: nothing here should move the
   * page. On wide screens both panes stay put and focus is left alone. */
  Calculator.prototype.showView = function (view) {
    this.root.setAttribute('data-dlc-view', view);
    if (sideBySide()) return;
    var target = view === 'result'
      ? this.results
      : this.root.querySelector('[data-dlc-ride]');
    if (target) target.focus({ preventScroll: true });
  };

  /* Longest | Shortest switch. Both cards are rendered; this shows one. */
  Calculator.prototype.showOption = function (which) {
    var buttons = this.results.querySelectorAll('[data-dlc-pick]');
    for (var i = 0; i < buttons.length; i++) {
      var on = buttons[i].getAttribute('data-dlc-pick') === which;
      buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    var cards = this.results.querySelectorAll('[data-dlc-card]');
    for (var j = 0; j < cards.length; j++) {
      cards[j].hidden = cards[j].getAttribute('data-dlc-card') !== which;
    }
  };

  /* The spinner goes on the button, not in the results area: the customer has
   * just pressed it, so it is the one place certain to be on screen. The
   * button is not disabled - that would drop keyboard focus to <body> - so the
   * busy flag is what stops a second press. The previous result stays where it
   * is, dimmed, rather than being emptied: emptying it would collapse the
   * result pane and jolt the layout for half a second. */
  Calculator.prototype.startLoading = function () {
    this.busy = true;
    this.results.setAttribute('aria-busy', 'true');
    if (!this.calcBtn) return;
    this.calcBtn.setAttribute('aria-disabled', 'true');
    this.calcBtn.classList.add('dlc__calculate--loading');
    this.calcBtn.innerHTML =
      '<span class="dlc__spinner" aria-hidden="true"></span>Calculating&hellip;';
  };

  /* After the first result the button becomes "Recalculate" and stays that way:
   * from here on, pressing it means checking new numbers, not getting an
   * answer for the first time. */
  Calculator.prototype.endLoading = function () {
    this.busy = false;
    this.results.removeAttribute('aria-busy');
    if (!this.calcBtn) return;
    this.calcBtn.removeAttribute('aria-disabled');
    this.calcBtn.classList.remove('dlc__calculate--loading');
    this.calcBtn.textContent = RECALCULATE_LABEL;
  };

  Calculator.prototype.render = function (input) {
    if (this.summary) {
      this.summary.textContent =
        input.diameter + ' · ride ' + input.ride + 'mm · insertion ' + input.insert + 'mm';
    }

    var drops = this.compatibleDrops(input.diameter, input.ride, input.insert);

    // Every drop that fits AND can be bought in this diameter.
    var options = [];
    var seen = {};
    for (var i = 0; i < drops.length; i++) {
      var hit = this.purchasableFor(drops[i].drop, input.diameter);
      if (!hit) continue;
      var key = hit.variant.id;
      if (seen[key]) continue;
      seen[key] = true;
      options.push(hit);
    }

    if (!drops.length) {
      this.renderNothingFits();
      return;
    }

    // Fits exist, but none can be bought in this diameter — 34.9mm x 90mm.
    // Re-run against 30.9mm, which a diameter shim can pack out to fit.
    if (!options.length) {
      this.renderShimFallback(input);
      return;
    }

    this.renderResults(options, input);
  };

  /* --- rendering --------------------------------------------------------- */

  Calculator.prototype.renderNothingFits = function () {
    this.results.innerHTML =
      '<div class="dlc__none">' +
      '<p>Your ride height, insertion depth or the combination of the two won\'t allow one of our posts to fit.</p>' +
      '<p>Please double-check both measurements (in millimetres) and try again, or <a href="/pages/contact">contact us</a> for help.</p>' +
      '</div>';
  };

  Calculator.prototype.renderResults = function (options, input) {
    var longest = options[0];
    var shortest = options[options.length - 1];
    var html = '<h3 class="dlc__results-heading">Recommended seatpost</h3>';

    // Tom's rule: lead with the longest, and offer the shortest without
    // showing both at once. A two-way switch rather than a reveal, so only
    // one card is ever on screen and the pane never grows to two cards.
    if (shortest.variant.id !== longest.variant.id) {
      html +=
        '<div class="dlc__switch" role="group" aria-label="Which compatible post to show">' +
        '<button type="button" class="dlc__switch-btn" data-dlc-pick="longest" aria-pressed="true">Longest</button>' +
        '<button type="button" class="dlc__switch-btn" data-dlc-pick="shortest" aria-pressed="false">Shortest / lightest</button>' +
        '</div>' +
        '<div data-dlc-card="longest">' +
        this.resultCard(longest, input, 'Longest compatible dropper') +
        '</div>' +
        '<div data-dlc-card="shortest" hidden>' +
        this.resultCard(shortest, input, 'Shortest compatible dropper') +
        '</div>';
    } else {
      html += this.resultCard(longest, input, 'Longest compatible dropper');
    }

    html +=
      '<p class="dlc__disclaimer"><strong>Important note:</strong> Always confirm ' +
      'measurements on your bike. Frame tolerances and manufacturing ' +
      'inconsistencies may vary.</p>';

    this.results.innerHTML = html;
  };

  Calculator.prototype.resultCard = function (option, input, label) {
    var v = option.variant;
    var html = '<div class="dlc__result">';
    html += '<p class="dlc__result-label">' + label + '</p>';
    // Headline the drop the rider ends up with. When the travel has to be
    // reduced that is less than the post's own travel, so the line beneath
    // names the post to buy.
    var drop = option.reduceTo !== null ? option.reduceTo + 'mm' : v.travel;
    html += '<p class="dlc__result-size">' + drop + ' drop</p>';
    html += '<p class="dlc__result-meta">V3 Dropper Post ' + v.travel + ' &middot; ' + v.diameter + '</p>';

    if (option.reduceTo !== null) {
      html +=
        '<p class="dlc__note">You will likely need to reduce the travel of the ' +
        option.base +
        'mm post to ' +
        option.reduceTo +
        'mm using the included travel reducing pins.</p>';
    }

    if (!v.available) {
      html +=
        '<p class="dlc__note dlc__note--stock">This option is currently out of stock.</p>' +
        '<a class="dlc__action" href="' +
        v.url +
        '">Notify me when in stock</a>';
      html += '</div>';
      return html;
    }

    html += this.actionFor(v);
    html += '</div>';
    return html;
  };

  /* What the customer can do about a result depends on where the calculator
   * is being shown:
   *   product  the V3 Dropper Post page — set its variant picker
   *   bundle   the V3 Bundle page — Rebuy owns the dropdowns, so we tell the
   *            customer what to pick and touch nothing
   *   page     the standalone selector page — link through with the option
   *            already chosen
   */
  Calculator.prototype.actionFor = function (v) {
    if (this.context === 'bundle') {
      return (
        '<p class="dlc__result-meta">Select <strong>' +
        v.diameter +
        '</strong> and <strong>' +
        v.travel +
        '</strong> in the options below to add this to your bundle.</p>'
      );
    }

    if (this.context === 'product') {
      return (
        '<button type="button" class="dlc__action" data-dlc-choose' +
        ' data-dlc-diameter-value="' + v.diameter + '"' +
        ' data-dlc-travel-value="' + v.travel + '"' +
        ' data-dlc-variant-url="' + v.url + '">Choose this option</button>'
      );
    }

    return '<a class="dlc__action" href="' + v.url + '">Choose this option</a>';
  };

  Calculator.prototype.renderShimFallback = function (input) {
    var drops = this.compatibleDrops(SHIM_BASE_DIAMETER, input.ride, input.insert);
    var options = [];
    for (var i = 0; i < drops.length; i++) {
      var hit = this.purchasableFor(drops[i].drop, SHIM_BASE_DIAMETER);
      if (hit) options.push(hit);
    }

    if (!options.length) {
      this.renderNothingFits();
      return;
    }

    // The shim is a seat post shim (diameter adapter), which no OneUp store
    // sells - say so plainly. "Shim" alone is ambiguous here: the V3's own
    // "What's in the Box" calls the travel reducing pins "shims".
    var pick = options[0];
    var html = '<h3 class="dlc__results-heading">Recommended seatpost</h3>';
    html +=
      '<p class="dlc__note dlc__note--shim">We don\'t make a ' +
      input.diameter +
      ' post that fits your measurements. The ' +
      SHIM_BASE_DIAMETER +
      ' post below will fit your frame with a ' +
      SHIM_BASE_DIAMETER +
      ' to ' +
      input.diameter +
      ' seat post shim.</p>';
    html += this.resultCard(pick, input, 'Seat post shim required, available from other brands');
    html +=
      '<p class="dlc__disclaimer"><strong>Important note:</strong> Always confirm ' +
      'measurements on your bike. Frame tolerances and manufacturing ' +
      'inconsistencies may vary.</p>';

    this.results.innerHTML = html;
  };

  /* --- setting the product page's variant picker --------------------------
   * The V3 has two options, so unlike the adapter/clamp quiz there is no single
   * input carrying the whole variant title. We set each option in turn, and
   * must wait between them: choosing a diameter re-renders the travel picker,
   * so a reference taken beforehand is stale by the time we would use it.
   *
   * If anything about that markup has changed, fall back to a plain ?variant=
   * navigation, which Shopify honours natively. Worst case the customer gets a
   * page load; never a dead button.
   */
  Calculator.prototype.chooseVariant = function (btn) {
    var self = this;
    var diameter = btn.getAttribute('data-dlc-diameter-value');
    var travel = btn.getAttribute('data-dlc-travel-value');
    var url = btn.getAttribute('data-dlc-variant-url');

    if (!this.clickOption(this.data.diameterOption, diameter)) {
      window.location.href = url;
      return;
    }

    window.setTimeout(function () {
      if (!self.clickOption(self.data.travelOption, travel)) {
        window.location.href = url;
        return;
      }

      // Confirm it actually took rather than assuming it did.
      window.setTimeout(function () {
        if (!self.isSelected(self.data.travelOption, travel)) {
          window.location.href = url;
          return;
        }
        // The choice has taken: close the pop-up so the customer is back on
        // the product page with that option selected. Closing through the
        // dialog's own close button releases the scroll lock and returns
        // focus to the trigger, exactly as a manual close does.
        var dialog = self.root.closest('dialog');
        var closer = dialog && dialog.querySelector('[data-dlc-close]');
        if (closer) closer.click();
      }, 250);
    }, 250);
  };

  Calculator.prototype.optionInputs = function (optionName) {
    var all = document.querySelectorAll(
      'product-option-picker input[type="radio"], product-option-picker select'
    );
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].name === optionName) out.push(all[i]);
    }
    return out;
  };

  Calculator.prototype.clickOption = function (optionName, value) {
    var inputs = this.optionInputs(optionName);
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];

      if (el.tagName === 'SELECT') {
        var matched = false;
        for (var k = 0; k < el.options.length; k++) {
          if (el.options[k].value === value) {
            el.selectedIndex = k;
            matched = true;
            break;
          }
        }
        if (!matched) continue;
        el.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      if (el.value !== value) continue;
      if (el.disabled) return false;

      // Click the label, not the input: the theme hides the input and reacts
      // to the label, and setting .checked directly fires no event at all.
      var label = document.querySelector('label[for="' + el.id + '"]');
      (label || el).click();
      return true;
    }
    return false;
  };

  Calculator.prototype.isSelected = function (optionName, value) {
    var inputs = this.optionInputs(optionName);
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el.tagName === 'SELECT') {
        if (el.value === value) return true;
        continue;
      }
      if (el.value === value && el.checked) return true;
    }
    return false;
  };

  /* --- modal ------------------------------------------------------------- */

  /* The product page's main-product section carries custom_css scoped to the
   * section id - `span { font-size: 20px }`, `a { font-size: 20px }`,
   * `button { max-width: 400px }`. An id-scoped rule beats any class selector,
   * so while the dialog lives inside that section its typography is not ours
   * to control: sentence fragments render at different sizes and full-width
   * buttons get capped at 400px.
   *
   * Moving the dialog to <body> takes it out of that subtree entirely, which
   * is the only reliable fix. It has to happen before anything is measured or
   * shown, and the node must be re-parented rather than cloned so the
   * listeners bound below stay attached. */
  function detachToBody(dialog) {
    if (dialog.parentNode === document.body) return;
    var existing = document.querySelector('body > [data-dlc-modal][id="' + dialog.id + '"]');
    if (existing && existing !== dialog) existing.parentNode.removeChild(existing);
    document.body.appendChild(dialog);
  }

  /* Images inside a dialog that carry data-dlc-src / data-dlc-srcset get them
   * copied across the first time the dialog opens - until then the browser
   * has no URL to fetch. This is what keeps the measurement diagram off the
   * wire for everyone who never asks to see it. */
  function loadDeferredImages(dialog) {
    var imgs = dialog.querySelectorAll('img[data-dlc-src]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var srcset = img.getAttribute('data-dlc-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', img.getAttribute('data-dlc-src'));
      img.removeAttribute('data-dlc-src');
      img.removeAttribute('data-dlc-srcset');
    }
  }

  /* The diagram dialog can open on top of the calculator dialog, so the page's
   * scroll lock is released only when the last of them closes - closing the
   * diagram must not unlock the page behind a calculator that is still open. */
  function releaseScrollLock() {
    if (!document.querySelector('[data-dlc-modal][open]')) {
      document.body.classList.remove('dlc-modal-open');
    }
  }

  function setupModal(dialog) {
    var opener = null;
    var supportsModal = typeof dialog.showModal === 'function';

    detachToBody(dialog);

    function open(trigger) {
      opener = trigger;
      loadDeferredImages(dialog);
      if (supportsModal) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.classList.add('dlc-modal--fallback');
      }
      document.body.classList.add('dlc-modal-open');

      var first = dialog.querySelector('[data-dlc-autofocus]') ||
        dialog.querySelector('[data-dlc-diameter]:checked, [data-dlc-ride]');
      if (first) first.focus({ preventScroll: true });
    }

    function close() {
      if (supportsModal && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.classList.remove('dlc-modal--fallback');
      }
      releaseScrollLock();
      // Return focus where the customer left the page, or the trigger is lost
      // to anyone navigating by keyboard.
      if (opener) opener.focus({ preventScroll: true });
    }

    var triggers = document.querySelectorAll('[data-dlc-open="' + dialog.id + '"]');
    for (var i = 0; i < triggers.length; i++) {
      (function (trigger) {
        trigger.addEventListener('click', function () {
          open(trigger);
        });
      })(triggers[i]);
    }

    var closers = dialog.querySelectorAll('[data-dlc-close]');
    for (var j = 0; j < closers.length; j++) {
      closers[j].addEventListener('click', close);
    }

    // Clicking the backdrop closes. The dialog element itself covers only the
    // panel, so a click landing on it is a click outside the content.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('close', function () {
      releaseScrollLock();
      if (opener) opener.focus({ preventScroll: true });
    });

    if (!supportsModal) {
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && dialog.hasAttribute('open')) close();
      });
    }
  }

  /* --- init -------------------------------------------------------------- */

  function init() {
    var roots = document.querySelectorAll('[data-dlc-root]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-dlc-ready') === 'true') continue;
      roots[i].setAttribute('data-dlc-ready', 'true');
      new Calculator(roots[i]);
    }

    var dialogs = document.querySelectorAll('[data-dlc-modal]');
    for (var j = 0; j < dialogs.length; j++) {
      if (dialogs[j].getAttribute('data-dlc-modal-ready') === 'true') continue;
      dialogs[j].setAttribute('data-dlc-modal-ready', 'true');
      setupModal(dialogs[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme re-renders sections in the Theme Editor without a page load.
  document.addEventListener('shopify:section:load', init);
})();
